import React from 'react';
import { CreditCard, Sparkles, ArrowRight, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { OPEN_FEEDBACK_EVENT } from '../Feedback/Feedback';

const HomeBanner = React.memo(() => {
  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };
  
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  // Function to handle feedback button click
  const handleFeedbackClick = (e) => {
    e.preventDefault();
    // Trigger the omnipresent feedback button via custom event
    window.dispatchEvent(new Event(OPEN_FEEDBACK_EVENT));
  };

  return (
    <motion.div 
      initial="hidden"
      animate="show"
      variants={container}
      className="relative overflow-hidden bg-[#E1FB5F] rounded-3xl shadow-xl"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#0F172A_1px,transparent_1px)] bg-[length:16px_16px]"></div>
      
      {/* Content wrapper */}
      <div className="relative z-10 px-6 py-10 md:px-12 md:py-14">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            {/* Left side - Credit info */}
            <motion.div variants={item} className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="bg-[#0F172A]/10 p-4 rounded-xl backdrop-blur-sm w-16 h-16 flex items-center justify-center">
                <CreditCard className="h-8 w-8 text-[#0F172A]" />
              </div>
              
              <div>
                <motion.div 
                  className="flex items-center gap-2"
                  variants={item}
                >
                  <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] font-display">Get 100 FREE Credits!</h2>
                  <Sparkles className="h-6 w-6 text-[#0F172A]" />
                </motion.div>
                
                <motion.p 
                  variants={item}
                  className="text-lg text-[#0F172A]/80 font-display mt-2"
                >
                  Sign up today and start creating magical stories with <span className="font-bold bg-[#0F172A]/10 backdrop-blur-sm px-3 py-1 rounded-full">KuchiPuchi's AI</span>
                </motion.p>
                
                <motion.p
                  variants={item}
                  className="text-sm text-[#0F172A]/70 font-display mt-1"
                >
                  Pro plans coming very soon! Until then, if you need more credits, please <a href="/contact" className="underline font-semibold hover:text-[#0F172A]">contact us</a>.
                </motion.p>
                
                <motion.p
                  variants={item}
                  className="text-sm text-[#0F172A]/70 font-display mt-2 flex items-center"
                >
                  <span className="bg-[#0F172A]/5 p-1.5 rounded-md flex items-center justify-center">
                    <MessageCircle className="h-3.5 w-3.5 text-[#0F172A]" />
                  </span>
                  <span className="ml-2"><button onClick={handleFeedbackClick} className="underline font-semibold hover:text-[#0F172A] inline-flex items-center gap-1">
                    Tell us what you think
                  </button>! We'd <span className="text-red-500">❤️</span> to hear your feedback!</span>
                </motion.p>
              </div>
            </motion.div>
            
            {/* Right side - CTA button */}
            <motion.button 
              variants={item}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#0F172A] text-white font-bold rounded-full hover:shadow-lg transition-all duration-300 shadow-md font-display group"
              onClick={() => window.location.href = '/app'}
            >
              <span>Get Started Now</span>
              <ArrowRight className="h-5 w-5 transition-all group-hover:translate-x-1" />
            </motion.button>
          </div>
        </div>
      </div>
      
      {/* Decorative elements */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0F172A]/0 via-[#0F172A]/10 to-[#0F172A]/0"></div>
    </motion.div>
  );
});

export default HomeBanner; 